import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Text } from '@atlaskit/primitives/compiled/text';
import StatusVerifiedIcon from '@atlaskit/icon/core/status-verified';
import { token } from '@atlaskit/tokens';

import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { Link } from '@confluence/route-manager/entry-points/Link.ts';
import { SearchBreadcrumb } from '@confluence/search-breadcrumbs/entry-points/SearchBreadcrumb.ts';
import { TimeAgo } from '@confluence/time-ago';
import {
	enrichExcerpt,
	cleanExcerpt,
	processExcerpt,
} from '@confluence/search-utils/entry-points/excerpt.ts';
import { QuickSummaryPopup } from '@confluence/quick-summary/entry-points/QuickSummaryPopup.tsx';
import { fg } from '@confluence/feature-gating';

import type { AdvancedSearchResultType } from '../../AdvancedSearch-types.ts';
import { SearchResultType } from '../../gql/__types__/AdvancedSearchAGGTypes.ts';
import { Icon as ResultIcon } from '../Icon.tsx';
import { CONTEXT_PATH } from '../../AdvancedSearch-constants.ts';
import { i18n } from '../../AdvancedSearch-i18n.ts';

import {
	LastModifiedWrapper,
	ResultContainer,
	ResultMainContainer,
	ResultIconContainer,
	ResultTitleMetadataContainer,
	ResultTitle,
	ResultMetadata,
	ResultExcerptContainer,
} from './styled.tsx';

interface ContentResultProps {
	searchResult: AdvancedSearchResultType;
	searchResultIdx: number;
	onClickResult: () => void;
	onContextClickResult: () => void;
}

export const ContentResult = ({
	searchResult,
	searchResultIdx,
	onClickResult,
	onContextClickResult,
}: ContentResultProps): React.JSX.Element => {
	const {
		entityType,
		title,
		subtype,
		excerpt,
		url,
		lastModified,
		content: {
			id: contentId,
			type: contentType,
			space: { key: spaceKey },
		},
		ancestors,
		emoji,
		isVerified,
	} = searchResult;

	const [isHovered, setHovered] = useState(false);
	const [isPopupOpen, setIsPopupOpen] = useState(false);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isAdminHubAIEnabled, edition } = useSessionData();
	const [{ searchSessionId }] = useSearchSessionId();

	const lastModifiedText =
		entityType === SearchResultType.attachment
			? i18n.resultLastModifiedAttachment
			: i18n.resultLastModifiedContent;

	const onOpen = () => {
		setIsPopupOpen(true);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'advancedSearch',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'quickSummaryAdvancedSearchButton',
				attributes: {
					contentId,
					isAIOptedIn: isAdminHubAIEnabled,
					edition,
					rowIndex: searchResultIdx,
					searchSessionId,
				},
			},
		}).fire();
	};

	const ResultTitleWithLink = (
		<ResultTitle>
			<Link
				href={`${CONTEXT_PATH}${url}`}
				onClick={onClickResult}
				onContextMenu={onContextClickResult}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="searchResultLink"
			>
				{enrichExcerpt(title)}
			</Link>
		</ResultTitle>
	);

	const VerifiedBadge = (
		<Flex alignItems="center">
			<Text>&#12539;</Text>
			<StatusVerifiedIcon label="verified" color={token('color.icon.accent.blue')} />
		</Flex>
	);

	return (
		<ResultContainer
			onMouseOver={() => setHovered(true)}
			onFocus={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			onBlur={() => setHovered(false)}
		>
			<ResultMainContainer>
				<ResultIconContainer>
					<ResultIcon
						contentType={entityType}
						subType={subtype}
						resultTitle={cleanExcerpt(title)}
						emojiValue={emoji}
					/>
				</ResultIconContainer>
				<ResultTitleMetadataContainer>
					{ResultTitleWithLink}
					<ResultMetadata>
						<SearchBreadcrumb
							index={searchResultIdx}
							contentId={contentId}
							spaceKey={spaceKey || ''}
							ancestors={ancestors}
						/>
						<LastModifiedWrapper>
							<Text>&#12539;</Text>
							<FormattedMessage {...lastModifiedText} /> <TimeAgo date={lastModified} />
						</LastModifiedWrapper>
						{isVerified && VerifiedBadge}
					</ResultMetadata>
				</ResultTitleMetadataContainer>
				{(isHovered || isPopupOpen) && fg('central_ai_quick_summary_in_advanced_search') && (
					<QuickSummaryPopup
						source="advancedSearch"
						contentPath={`${CONTEXT_PATH}${url}`}
						contentId={contentId}
						spaceKey={spaceKey || undefined}
						contentType={contentType}
						lastModifiedDate={lastModified}
						onOpen={() => onOpen()}
						onClose={() => setIsPopupOpen(false)}
					/>
				)}
			</ResultMainContainer>
			<ResultExcerptContainer>{processExcerpt(excerpt)}</ResultExcerptContainer>
		</ResultContainer>
	);
};
